import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import Confetti from "react-confetti";
import { SpinAWheelApi } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";

const SEGMENT_COLORS = [
  // Soft, modern pastel palette for public page
  "#f6e3ff",
  "#e3f9ff",
  "#ffeec2",
  "#ffe0e6",
  "#e0f7d9",
  "#e2ebff",
  "#ffe9cc",
  "#f8e5ff",
];

interface SpinWheelSlot {
  id: number;
  slotName: string;
  points: number;
  spinConfigId: number;
}

interface SpinWheelConfig {
  id: number;
  spinName: string;
  startDate: string;
  endDate: string;
  noOfSlots: number;
  spinPerMember: number;
  bonusValidityInDay: number;
  nonWinningSlots: number;
  spinAWheelSlots: SpinWheelSlot[];
}

// Public-only spin wheel layout with large glassmorphism wheel and center "Engage" button
export function PublicSpinWheelContent() {
  const { toast } = useToast();
  const [mobile, setMobile] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiSize, setConfettiSize] = useState({ width: 0, height: 0 });
  const [isCollecting, setIsCollecting] = useState(false);
  const [mustCollectPoints, setMustCollectPoints] = useState(false);
  const [spinWheelConfig, setSpinWheelConfig] = useState<SpinWheelConfig | null>(null);

  // Fetch spin wheel config by client ID on mount
  useEffect(() => {
    fetchSpinWheelConfigByClientId();
  }, []);

  const fetchSpinWheelConfigByClientId = async () => {
    try {
      const response = await SpinAWheelApi.getSpinAwheelConfigByClientId({
        p_ClientId: 1009,
      });
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else if (response.clientSpinAwheelConfig) {
        const config = response.clientSpinAwheelConfig;
        setSpinWheelConfig({
          id: config.id || 0,
          spinName: config.spinName || "",
          startDate: config.startDate || "",
          endDate: config.endDate || "",
          noOfSlots: config.noOfSlots || 0,
          spinPerMember: config.spinPerMember || 0,
          bonusValidityInDay: config.bonusValidityInDay || 0,
          nonWinningSlots: config.nonWinningSlots || 0,
          spinAWheelSlots: config.spinAWheelSlots || (config as any).listWheelSlots || [],
        });
      }
    } catch (error: any) {
      console.error("Error fetching spin wheel config:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch spin wheel configuration",
        variant: "destructive",
      });
    }
  };

  const selectedSpinWheel = spinWheelConfig;

  const hasSlots = useMemo(() => {
    return (
      !!selectedSpinWheel &&
      !!selectedSpinWheel.spinAWheelSlots &&
      selectedSpinWheel.spinAWheelSlots.length > 0
    );
  }, [selectedSpinWheel]);

  const SEGMENTS = useMemo(() => {
    if (!selectedSpinWheel || !hasSlots) {
      return [0];
    }
    const points = selectedSpinWheel.spinAWheelSlots.map((slot) => slot.points);
    const totalSlots = selectedSpinWheel.noOfSlots;
    const currentSlots = points.length;
    if (currentSlots < totalSlots) {
      const additionalSlots = totalSlots - currentSlots;
      const nonWinningSlots = Array(additionalSlots).fill(0);
      return [...points, ...nonWinningSlots];
    }
    return points;
  }, [selectedSpinWheel, hasSlots]);

  const segmentAngle = 360 / SEGMENTS.length;

  const paths = useMemo(() => {
    const radius = 220;
    const center = 0;
    return SEGMENTS.map((_, index) => {
      const startAngle = ((index * segmentAngle - 90) * Math.PI) / 180;
      const endAngle = (((index + 1) * segmentAngle - 90) * Math.PI) / 180;
      const x1 = center + radius * Math.cos(startAngle);
      const y1 = center + radius * Math.sin(startAngle);
      const x2 = center + radius * Math.cos(endAngle);
      const y2 = center + radius * Math.sin(endAngle);
      const largeArcFlag = segmentAngle > 180 ? 1 : 0;
      return {
        d: `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`,
      };
    });
  }, [segmentAngle, SEGMENTS.length]);

  useEffect(() => {
    const updateSize = () => {
      if (typeof window !== "undefined") {
        setConfettiSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);


  const handleSendOtp = () => {
    if (!mobile || mobile.length < 7 || mobile.length > 15) {
      toast({
        title: "Invalid mobile number",
        description: "Please enter a valid mobile number (7-15 digits).",
        variant: "destructive",
      });
      return;
    }
    setOtpSent(true);
    setIsOtpVerified(false);
    setOtp("");
    toast({
      title: "OTP sent",
      description: "A test OTP has been 'sent' to the entered mobile number.",
    });
  };

  const handleVerifyOtp = () => {
    if (!otp || otp.length < 4) {
      toast({
        title: "Invalid OTP",
        description: "Please enter a valid OTP.",
        variant: "destructive",
      });
      return;
    }
    if (otp !== "1992") {
      toast({
        title: "Incorrect OTP",
        description: "Please enter the correct OTP.",
        variant: "destructive",
      });
      setIsOtpVerified(false);
      return;
    }
    setIsOtpVerified(true);
    toast({
      title: "OTP verified",
      description: "You can now spin the wheel.",
    });
  };

  const handleSpin = () => {
    if (!isOtpVerified || isSpinning || mustCollectPoints || !selectedSpinWheel) return;
    const extraSpins = 10;
    const randomOffset = Math.random() * 360;
    const finalRotation = rotation + extraSpins * 360 + randomOffset;
    setIsSpinning(true);
    setRotation(finalRotation);
    setSelectedIndex(null);
    setMustCollectPoints(false);

    setTimeout(() => {
      setIsSpinning(false);
      const pointerAngle = 180;
      const normalizedRotation = ((finalRotation % 360) + 360) % 360;
      let winningIndex = 0;
      let minDiff = Infinity;
      SEGMENTS.forEach((_, index) => {
        const centerAngle = -90 + (index + 0.5) * segmentAngle;
        const screenAngle =
          (((centerAngle + normalizedRotation) % 360) + 360) % 360;
        const rawDiff = Math.abs(screenAngle - pointerAngle);
        const diff = Math.min(rawDiff, 360 - rawDiff);
        if (diff < minDiff) {
          minDiff = diff;
          winningIndex = index;
        }
      });
      setSelectedIndex(winningIndex);
      const pointsWon = SEGMENTS[winningIndex];
      if (pointsWon > 0) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 8000);
      }
      setMustCollectPoints(true);
      toast({
        title: "Spin Result",
        description: `You won ${SEGMENTS[winningIndex]} points!`,
      });
    }, 6000);
  };

  const handleCollectPoints = async () => {
    if (selectedIndex === null || !selectedSpinWheel) {
      return;
    }
    const pointsWon = SEGMENTS[selectedIndex];
    if (!mobile || !isOtpVerified) {
      toast({
        title: "Verification required",
        description: "Please verify your mobile number and OTP before collecting points.",
        variant: "destructive",
      });
      return;
    }
    setIsCollecting(true);
    setMustCollectPoints(false);
    try {
      const payload = {
        p_MobileNo: mobile,
        p_OTP: "1992",
        p_PointsWon: pointsWon,
        p_SpinId: selectedSpinWheel.id,
      };
      const response = await SpinAWheelApi.insertSpinAWheelTransaction(payload);
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Points Collected",
          description: response.message || "Points collected successfully.",
        });
        // Reset wheel back to initial position and clear last result
        setSelectedIndex(null);
        setRotation(0);
        setMustCollectPoints(false);
        setShowConfetti(false);
        // Reset OTP verification
        setIsOtpVerified(false);
        setOtpSent(false);
        setOtp("");
      }
    } catch (error: any) {
      console.error("Error collecting points:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to collect points.",
        variant: "destructive",
      });
    } finally {
      setIsCollecting(false);
    }
  };

  return (
    <div className="space-y-6 relative">
      {showConfetti && confettiSize.width > 0 && confettiSize.height > 0 && (
        <Confetti
          width={confettiSize.width}
          height={confettiSize.height}
          numberOfPieces={300}
          recycle={false}
        />
      )}
      {/* Wheel & Result Section */}
      <div className="grid gap-6 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] items-start">
        <div className="flex flex-col items-center gap-8">
          <div className="relative w-[32rem] h-[32rem] flex items-center justify-center">
            {!isOtpVerified ? (
              <div className="w-[28rem] h-[28rem] rounded-full border-[1px] border-white/40 bg-white/10 backdrop-blur-xl shadow-[0_18px_45px_rgba(15,23,42,0.35)] flex items-center justify-center">
                <p className="text-sm text-black text-center px-4">
                  Verify your mobile number to unlock your spin
                </p>
              </div>
            ) : !selectedSpinWheel ? (
              // After OTP, but config not loaded or missing
              <div className="w-[28rem] h-[28rem] rounded-full border-[1px] border-yellow-100/60 bg-yellow-50/20 backdrop-blur-xl shadow-[0_18px_45px_rgba(234,179,8,0.35)] flex items-center justify-center">
                <p className="text-sm text-yellow-800 text-center px-4">
                  Spin wheel configuration is not available. Please try again later.
                </p>
              </div>
            ) : !hasSlots ? (
              <div className="w-[28rem] h-[28rem] rounded-full border-[1px] border-red-100/60 bg-red-50/20 backdrop-blur-xl shadow-[0_18px_45px_rgba(248,113,113,0.35)] flex items-center justify-center">
                <div className="text-center px-4">
                  <p className="text-sm font-medium text-red-600 mb-1">
                    ⚠️ Slots Not Configured
                  </p>
                  <p className="text-xs text-red-500">
                    This spin wheel does not have any slots configured. Please configure slots before spinning.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Wheel */}
                <div
                  className="w-[28rem] h-[28rem] rounded-full border border-white/60 bg-white/10 backdrop-blur-2xl shadow-[0_24px_65px_rgba(15,23,42,0.45)] flex items-center justify-center"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transition: "transform 6s cubic-bezier(0.15, 0.85, 0.25, 1)",
                  }}
                >
                  <svg
                    width="480"
                    height="480"
                    viewBox="-240 -240 480 480"
                    className="overflow-visible"
                  >
                    {paths.map((path, index) => (
                      <path
                        key={index}
                        d={path.d}
                        fill={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
                        stroke="#e5e7eb"
                        strokeWidth="1"
                      />
                    ))}
                    {SEGMENTS.map((points, index) => {
                      const angle =
                        ((index + 0.5) * segmentAngle * Math.PI) / 180 -
                        Math.PI / 2;
                      const labelRadius = 140;
                      const x = labelRadius * Math.cos(angle);
                      const y = labelRadius * Math.sin(angle);
                      const slot = selectedSpinWheel?.spinAWheelSlots?.[index];
                      const slotName = slot?.slotName || "";
                      return (
                        <text
                          key={index}
                          x={x}
                          y={y}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="text-xs font-semibold fill-gray-800"
                        >
                          {points > 0 ? `${points} pts` : "0"}
                          {slotName && points > 0 && (
                            <tspan
                              x={x}
                              dy="12"
                              className="text-[10px] fill-gray-600"
                            >
                              {slotName}
                            </tspan>
                          )}
                        </text>
                      );
                    })}
                  </svg>

                  {/* Center Engage Button */}
                  <button
                    type="button"
                    onClick={handleSpin}
                    disabled={
                      !isOtpVerified ||
                      isSpinning ||
                      mustCollectPoints ||
                      !selectedSpinWheel ||
                      !hasSlots
                    }
                    className="absolute w-32 h-32 rounded-full bg-gradient-to-b from-[#34d27b] to-[#16a34a] text-white font-semibold shadow-[0_22px_55px_rgba(34,197,94,0.85)] flex items-center justify-center text-base tracking-wide border border-white/60 backdrop-blur-xl disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSpinning ? "Spinning..." : "Engage"}
                  </button>
                </div>

                {/* Pointer */}
                <div className="absolute top-1/2 -left-1 transform -translate-y-1/2">
                  <div className="w-0 h-0 border-y-[8px] border-y-transparent border-l-[16px] border-l-red-500" />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right side: Mobile, selection, result */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Mobile Verification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Mobile Number</label>
                <div className="flex gap-2">
                  <Input
                    type="tel"
                    placeholder="Enter mobile number"
                    value={mobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      setMobile(value);
                    }}
                    maxLength={15}
                  />
                  <Button type="button" variant="outline" onClick={handleSendOtp}>
                    Send OTP
                  </Button>
                </div>
              </div>

              {otpSent && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Enter OTP</label>
                  <div className="flex gap-2">
                    <Input
                      type="tel"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setOtp(value);
                      }}
                      maxLength={6}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleVerifyOtp}
                    >
                      Verify
                    </Button>
                  </div>
                  {isOtpVerified && (
                    <p className="text-xs text-emerald-600 font-medium">
                      OTP verified. You can spin the wheel now.
                    </p>
                  )}
                </div>
              )}

              {isOtpVerified && selectedSpinWheel && !hasSlots && (
                <div className="space-y-2">
                  <p className="text-xs text-red-600 font-medium">
                    ⚠️ Slots are not configured for this spin wheel. Please configure
                    slots before spinning.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Result</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Spin the wheel to see how many points you win.
              </p>
              <div className="mt-2 p-4 border rounded-md bg-gray-50 flex flex-col items-center justify-center min-h-[80px]">
                {selectedIndex !== null ? (
                  <>
                    <p className="text-sm text-gray-600 mb-1">You won</p>
                    <p className="text-3xl font-bold text-emerald-600">
                      {SEGMENTS[selectedIndex]} points
                    </p>
                    {SEGMENTS[selectedIndex] === 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Better luck next time!
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-500">
                    No spins yet. Verify your mobile and click Engage.
                  </p>
                )}
              </div>
              {selectedIndex !== null && (
                <Button
                  type="button"
                  className="w-full mt-2"
                  onClick={handleCollectPoints}
                  disabled={isCollecting}
                >
                  {isCollecting ? "Collecting..." : "Collect Points"}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}


